import React, { useState, useEffect } from 'react';
import { 
  FileBarChart, Plus, Search, Calendar, TrendingUp, TrendingDown,
  ChevronRight, X, Loader2, CheckCircle, Clock, Eye, Download,
  BarChart3, Users, DollarSign, Mic, Heart, Star
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const REPORT_TYPES = {
  monthly: { label: 'Monthly', color: 'bg-blue-500' },
  quarterly: { label: 'Quarterly', color: 'bg-purple-500' },
  annual: { label: 'Annual', color: 'bg-green-500' },
  custom: { label: 'Custom', color: 'bg-orange-500' }
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: Clock },
  published: { label: 'Published', color: 'bg-green-500', icon: CheckCircle }
};

const AEReports = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchReports();
    }
  }, [userEmail, filterType]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/reports?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        let filtered = data.data;
        if (filterType !== 'all') {
          filtered = filtered.filter(r => r.report_type === filterType);
        }
        setReports(filtered);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getDateRange = (report) => {
    if (!report.period_start || !report.period_end) return 'No date range';
    const start = new Date(report.period_start);
    const end = new Date(report.period_end);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-reports-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">
                {isAdmin ? 'ELLE MAXWELL AGENCY' : 'ALWAYS ELEVATE'}
              </p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-reports-title">
                {isAdmin ? 'Reports & Analytics' : 'Monthly Reports'}
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {reports.length} reports available
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                data-testid="create-report-btn"
              >
                <Plus className="w-4 h-4" />
                Create Report
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              filterType === 'all' ? 'bg-[#C4A052] text-black font-medium' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            All Reports
          </button>
          {Object.entries(REPORT_TYPES).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setFilterType(filterType === key ? 'all' : key)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                filterType === key ? `${config.color} text-white` : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>

        {/* Reports Grid */}
        {reports.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <FileBarChart className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Reports Found</h3>
            <p className="text-gray-500 text-sm">
              {isAdmin ? 'Create your first report' : 'No reports have been published yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
            {reports.map(report => {
              const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.monthly;
              const statusConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report)}
                  className="bg-black border border-white/20 rounded-xl overflow-hidden cursor-pointer transition-all"
                >
                  {/* Header */}
                  <div className={`${typeConfig.color} px-4 py-3`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{typeConfig.label} Report</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${statusConfig.color} text-white flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{report.title}</h3>
                    <p className="text-sm text-gray-400 mb-3">{getDateRange(report)}</p>
                    
                    {report.summary && (
                      <p className="text-sm text-gray-300 line-clamp-2 mb-4">{report.summary}</p>
                    )}
                    
                    {/* Quick Stats */}
                    {report.metrics && Object.keys(report.metrics).length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {report.metrics.engagement_rate && (
                          <div className="bg-white/5 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-[#C4A052]">{report.metrics.engagement_rate}%</p>
                            <p className="text-xs text-gray-400">Engagement</p>
                          </div>
                        )}
                        {report.metrics.content_published && (
                          <div className="bg-white/5 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold">{report.metrics.content_published}</p>
                            <p className="text-xs text-gray-400">Content</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-xs text-gray-500">
                        {new Date(report.created_at).toLocaleDateString()}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0f0f0f] z-10">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-2 py-1 rounded text-xs ${REPORT_TYPES[selectedReport.report_type]?.color || 'bg-gray-500'} text-white`}>
                    {REPORT_TYPES[selectedReport.report_type]?.label || selectedReport.report_type}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${STATUS_CONFIG[selectedReport.status]?.color || 'bg-gray-500'} text-white`}>
                    {STATUS_CONFIG[selectedReport.status]?.label || selectedReport.status}
                  </span>
                </div>
                <h3 className="text-2xl font-bold">{selectedReport.title}</h3>
                <p className="text-gray-400 text-sm">{getDateRange(selectedReport)}</p>
              </div>
              <button onClick={() => setSelectedReport(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Summary */}
              {selectedReport.summary && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Executive Summary</h4>
                  <p className="text-gray-300 leading-relaxed">{selectedReport.summary}</p>
                </div>
              )}

              {/* Metrics Dashboard */}
              {selectedReport.metrics && Object.keys(selectedReport.metrics).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-4">Key Metrics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedReport.metrics.social_followers && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <Users className="w-5 h-5 text-blue-400 mb-2" />
                        <p className="text-2xl font-bold">
                          {formatNumber(
                            (selectedReport.metrics.social_followers.instagram || 0) +
                            (selectedReport.metrics.social_followers.twitter || 0) +
                            (selectedReport.metrics.social_followers.tiktok || 0)
                          )}
                        </p>
                        <p className="text-xs text-gray-400">Total Followers</p>
                      </div>
                    )}
                    {selectedReport.metrics.engagement_rate && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <TrendingUp className="w-5 h-5 text-green-400 mb-2" />
                        <p className="text-2xl font-bold">{selectedReport.metrics.engagement_rate}%</p>
                        <p className="text-xs text-gray-400">Engagement Rate</p>
                      </div>
                    )}
                    {selectedReport.metrics.content_published && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <FileBarChart className="w-5 h-5 text-purple-400 mb-2" />
                        <p className="text-2xl font-bold">{selectedReport.metrics.content_published}</p>
                        <p className="text-xs text-gray-400">Content Published</p>
                      </div>
                    )}
                    {selectedReport.metrics.media_mentions && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <Mic className="w-5 h-5 text-cyan-400 mb-2" />
                        <p className="text-2xl font-bold">{selectedReport.metrics.media_mentions}</p>
                        <p className="text-xs text-gray-400">Media Mentions</p>
                      </div>
                    )}
                    {selectedReport.metrics.partnership_value && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <DollarSign className="w-5 h-5 text-[#C4A052] mb-2" />
                        <p className="text-2xl font-bold">{formatCurrency(selectedReport.metrics.partnership_value)}</p>
                        <p className="text-xs text-gray-400">Partnership Value</p>
                      </div>
                    )}
                    {selectedReport.metrics.community_reach && (
                      <div className="bg-black border border-white/20 rounded-xl p-4">
                        <Heart className="w-5 h-5 text-red-400 mb-2" />
                        <p className="text-2xl font-bold">{formatNumber(selectedReport.metrics.community_reach)}</p>
                        <p className="text-xs text-gray-400">Community Reach</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Social Breakdown */}
                  {selectedReport.metrics.social_followers && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-white/10 rounded-xl p-4 text-center">
                        <p className="text-lg font-bold">{formatNumber(selectedReport.metrics.social_followers.instagram)}</p>
                        <p className="text-xs text-gray-400">Instagram</p>
                      </div>
                      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-white/10 rounded-xl p-4 text-center">
                        <p className="text-lg font-bold">{formatNumber(selectedReport.metrics.social_followers.twitter)}</p>
                        <p className="text-xs text-gray-400">Twitter</p>
                      </div>
                      <div className="bg-gradient-to-r from-gray-500/20 to-white/10 border border-white/10 rounded-xl p-4 text-center">
                        <p className="text-lg font-bold">{formatNumber(selectedReport.metrics.social_followers.tiktok)}</p>
                        <p className="text-xs text-gray-400">TikTok</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Highlights */}
              {selectedReport.highlights && selectedReport.highlights.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Key Highlights</h4>
                  <div className="space-y-2">
                    {selectedReport.highlights.map((highlight, i) => (
                      <div key={i} className="flex items-start gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                        <Star className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-300">{highlight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedReport.recommendations && selectedReport.recommendations.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Recommendations</h4>
                  <div className="space-y-2">
                    {selectedReport.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 bg-[#C4A052]/10 border border-[#C4A052]/20 rounded-lg p-3">
                        <CheckCircle className="w-4 h-4 text-[#C4A052] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-300">{rec}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 flex justify-between sticky bottom-0 bg-[#0f0f0f]">
              <div className="text-sm text-gray-500">
                Created {new Date(selectedReport.created_at).toLocaleDateString()}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Close
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors">
                  <Download className="w-4 h-4" />
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (Admin) */}
      {showCreateModal && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Create Report</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 text-sm">Report creation form coming soon.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEReports;
